import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { GET_PATIENT_HEALTH, UPDATE_PATIENT_HEALTH } from '../../services/patient';

import { validPhone } from '../../utils/regex';
import { alertError, alertSuccess } from '../../redux/stateActions';

import {
    ContainedButton,
    OutlinedButton,
    Header,
    PersonalHealthForm,
    EmergencyForm,
    Progress,
    Text,
} from '../index';

import { StyledCard, StyledCardContent } from './style';

export const HealthCard = ({ mdUp, mobileS, onGoBack, $transparent }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [errors, setErrors] = useState([]);
    const [inputValue, setInputValue] = useState({
        avatar: '',
        weight: '',
        height: '',
        drugAllergy: '',
        chronicDeseases1: '',
        chronicDeseases2: '',
        pregnant: false,
        smoker: false,
        drinker: false,
        familyMedicalRecord: '',
        emergencyContractNumber: '',
        emergencyContractName: '',
        emergencyContractRelation: '',
    });

    //  Patient Personal Detail
    const {
        data: getPatientData,
        loading: getPatientLoading,
        error: getPatientError,
        refetch: getPatientRefetch,
    } = useQuery(GET_PATIENT_HEALTH, {
        variables: { id: user.patient.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getPatientError) {
            dispatch(alertError(`Cannot get Client: ${getPatientError.message}`));
        }
        if (getPatientData) {
            setInputValue(getPatientData.patient);
        }
    }, [getPatientError, getPatientData]);

    const [
        updatePatient,
        { data: updatePatientData, loading: updatePatientLoading, error: updatePatientError },
    ] = useMutation(UPDATE_PATIENT_HEALTH);

    useEffect(() => {
        if (updatePatientError) {
            dispatch(alertError(`Cannot update Client: ${updatePatientError.message}`));
        }
        if (updatePatientData) {
            dispatch(alertSuccess('Client Updated'));
            getPatientRefetch();
        }
    }, [updatePatientError, updatePatientData]);

    const handlePersonalHealthChange = (event) => {
        const newInputValue = { ...inputValue };
        const name = event.target.name;
        const value = event.target.value;
        if (name == 'weight' || name == 'height') {
            newInputValue[name] = parseFloat(value);
        } else if (['pregnant', 'smoker', 'drinker'].includes(name)) {
            newInputValue[name] = event.target.checked;
        } else {
            newInputValue[name] = value;
        }
        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleReset = () => {
        setErrors([])
        setInputValue(getPatientData.patient);
    };

    const handleSave = () => {
        const newErrors = [];

        if (!inputValue.weight || inputValue.weight === '' || inputValue.weight == 0) {
            newErrors.push({
                property: 'weight',
                message: 'Please provide a valid weight.',
            });
        }

        if (!inputValue.height || inputValue.height === '' || inputValue.height == 0) {
            newErrors.push({
                property: 'height',
                message: 'Please provide a valid height.',
            });
        }

        if (inputValue.emergencyContractNumber === '') {
            newErrors.push({
                property: 'emergencyContractNumber',
                message: 'Please provide a valid emergency Contract Number.',
            });
        }
        if (inputValue.emergencyContractName?.trim() === '') {
            newErrors.push({
                property: 'emergencyContractName',
                message: 'Please provide a valid emergency Contract Name.',
            });
        }

        if (inputValue.emergencyContractRelation?.trim() === '') {
            newErrors.push({
                property: 'emergencyContractRelation',
                message: 'Please provide a valid emergency Contract Relation.',
            });
        }

        if (!validPhone.test(inputValue.emergencyContractNumber)) {
            newErrors.push({
                property: 'emergencyContractNumber',
                message: 'Please provide a valid Phone Number.',
            });
        }

        if (!_.isEmpty(newErrors)) return setErrors(newErrors);

        updatePatient({ variables: inputValue });
    };

    return (
        <div>
            {!mdUp && (
                <Header
                    onClickGoBack={onGoBack}
                    showGoBack1
                    childrenContainerStyle={{ textAlign: 'center' }}
                >
                    <Text>Personal Health</Text>
                </Header>
            )}
            {(getPatientLoading || updatePatientLoading) && <Progress />}

            <StyledCard
                variant="outlined"
                padding={mobileS ? '15px 5px' : '20px'}
                $transparent={$transparent}
            >
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                >
                    <PersonalHealthForm
                        data={inputValue}
                        onChange={handlePersonalHealthChange}
                        errors={errors}
                    />
                    <EmergencyForm
                        data={inputValue}
                        onChange={handlePersonalHealthChange}
                        errors={errors}
                    />
                </StyledCardContent>
            </StyledCard>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: '20px',
                }}
            >
                <OutlinedButton $width="96px" $height="32px" onClick={handleReset}>
                    Reset
                </OutlinedButton>
                <ContainedButton
                    $width="96px"
                    $height="32px"
                    style={{ marginLeft: '15px' }}
                    onClick={handleSave}
                >
                    Save
                </ContainedButton>
            </div>
        </div>
    );
};

HealthCard.propTypes = {
    mdUp: PropTypes.bool,
    mobileS: PropTypes.bool,
    onGoBack: PropTypes.func,
    $transparent: PropTypes.bool,
};
